import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Palette, Save, Check, Loader2, KeyRound, Copy, Megaphone, Send } from "lucide-react";
import Layout from "../components/Layout";
import { api, auth } from "../lib/api";
import { useSettings } from "../lib/settings";
import { PRESET_COLORS } from "../lib/theme";
import { regenerateAdminPassword } from "../lib/adminActions";

export default function MasterPreferences() {
  const navigate = useNavigate();
  const user = auth.current();
  const { refresh } = useSettings();
  const [form, setForm] = useState({ agency_name: "Tulkas", tagline: "", primary_color: "#FF85AC", secondary_color: "#7C3AED" });
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const [loadFailed, setLoadFailed] = useState(false);
  const [newPassword, setNewPassword] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [pwCopied, setPwCopied] = useState(false);
  const [otherBookingId, setOtherBookingId] = useState("");
  const [otherResult, setOtherResult] = useState(null); // { booking_id, traveler_name, role, password }
  const [resettingOther, setResettingOther] = useState(false);
  const [otherErr, setOtherErr] = useState("");
  const [otherPwCopied, setOtherPwCopied] = useState(false);
  const [promo, setPromo] = useState({ title: "", subtitle: "", destination: "", price_from: "", currency: "USD" });
  const [sendingPromo, setSendingPromo] = useState(false);
  const [promoMsg, setPromoMsg] = useState("");
  const [promoErr, setPromoErr] = useState("");

  const loadPreferences = () => {
    setLoadFailed(false);
    api.get("/master/preferences").then(({ data }) => setForm(data)).catch(() => setLoadFailed(true));
  };

  useEffect(() => {
    if (!user || user.role !== "master") { navigate("/admin/login"); return; }
    loadPreferences();
  }, []);

  if (!user) return null;

  const submit = async () => {
    setErr("");
    setSaving(true);
    try {
      await api.put("/master/preferences", form);
      await refresh();
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const resetMyPassword = async () => {
    setResetting(true);
    setErr("");
    try {
      const data = await regenerateAdminPassword(user.booking_id);
      setNewPassword(data.password);
    } catch (e) {
      setErr(e?.response?.data?.detail || "No se pudo generar la contraseña");
    } finally {
      setResetting(false);
    }
  };

  const resetOtherPassword = async (e) => {
    e.preventDefault();
    setOtherErr("");
    setOtherResult(null);
    const bookingId = otherBookingId.trim().toUpperCase();
    if (!bookingId) return;
    setResettingOther(true);
    try {
      const data = await regenerateAdminPassword(bookingId);
      setOtherResult(data);
      setOtherBookingId("");
    } catch (e) {
      setOtherErr(e?.response?.data?.detail || "No se pudo generar la contraseña");
    } finally {
      setResettingOther(false);
    }
  };

  const sendPlatformPromo = async (e) => {
    e.preventDefault();
    setPromoErr("");
    setPromoMsg("");
    if (!window.confirm("Esto manda un push a TODOS los viajeros de TODAS las agencias, no solo una. ¿Seguro?")) return;
    setSendingPromo(true);
    try {
      const { data: created } = await api.post("/promotions", { ...promo, price_from: parseFloat(promo.price_from) || 0 });
      const { data: sent } = await api.post(`/promotions/${created.id}/send-push`);
      setPromoMsg(`Enviado a ${sent.sent_to} viajero(s) de toda la plataforma.`);
      setPromo({ title: "", subtitle: "", destination: "", price_from: "", currency: "USD" });
    } catch (e) {
      setPromoErr(e?.response?.data?.detail || "No se pudo enviar");
    } finally {
      setSendingPromo(false);
    }
  };

  return (
    <Layout>
      <section className="max-w-3xl mx-auto px-6 md:px-10 py-12 space-y-8">
        <div className="fade-up">
          <p className="text-xs uppercase tracking-[0.4em] text-[var(--brand-cyan,#FF85AC)] mb-3">Master · Tulkas</p>
          <h1 className="font-display text-5xl font-light tracking-tight">
            Mi <span className="text-[var(--brand-cyan,#FF85AC)]">Personalización</span>
          </h1>
          <p className="text-neutral-400 mt-3 max-w-xl">
            Solo afecta tus propias pantallas (Agencias, Reportes) — nunca la marca de ninguna
            agencia cliente.
          </p>
        </div>

        <article className="glass rounded-3xl p-7 border border-white/10">
          <div className="flex items-center gap-3 mb-5">
            <Palette className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Identidad y color</h2>
          </div>
          <div className="space-y-5">
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Nombre a mostrar</label>
              <input
                value={form.agency_name}
                onChange={(e) => setForm({ ...form, agency_name: e.target.value })}
                data-testid="master-display-name-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
              />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block mb-1.5">Tagline</label>
              <input
                value={form.tagline}
                onChange={(e) => setForm({ ...form, tagline: e.target.value })}
                data-testid="master-tagline-input"
                className="w-full bg-transparent border-b border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none py-2 text-white"
              />
            </div>

            <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block">Primario</label>
            <div className="flex items-center gap-4">
              <input type="color" value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                data-testid="master-primary-color" className="w-16 h-12 rounded-lg bg-transparent border border-white/15 cursor-pointer" />
              <input value={form.primary_color} onChange={(e) => setForm({ ...form, primary_color: e.target.value })}
                className="font-mono text-sm bg-transparent border-b border-white/15 outline-none py-2 text-white w-32" />
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button key={c} onClick={() => setForm({ ...form, primary_color: c })}
                  className={`w-9 h-9 rounded-full border-2 hover:scale-110 transition-transform ${form.primary_color?.toLowerCase() === c.toLowerCase() ? "border-white" : "border-transparent"}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>

            <label className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 block">Secundario</label>
            <div className="flex items-center gap-4">
              <input type="color" value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                data-testid="master-secondary-color" className="w-16 h-12 rounded-lg bg-transparent border border-white/15 cursor-pointer" />
              <input value={form.secondary_color} onChange={(e) => setForm({ ...form, secondary_color: e.target.value })}
                className="font-mono text-sm bg-transparent border-b border-white/15 outline-none py-2 text-white w-32" />
            </div>
            <div className="flex flex-wrap gap-2">
              {PRESET_COLORS.map((c) => (
                <button key={`s-${c}`} onClick={() => setForm({ ...form, secondary_color: c })}
                  className={`w-9 h-9 rounded-full border-2 hover:scale-110 transition-transform ${form.secondary_color?.toLowerCase() === c.toLowerCase() ? "border-white" : "border-transparent"}`}
                  style={{ backgroundColor: c }} />
              ))}
            </div>
          </div>

          {loadFailed && (
            <div className="mt-5 p-4 rounded-2xl bg-[#FF3B30]/10 border border-[#FF3B30]/30" data-testid="master-prefs-load-error">
              <p className="text-sm text-[#FF3B30]">
                No se pudo cargar tu personalización guardada — lo que ves arriba puede NO ser lo real.
                Guardar ahora sobreescribiría tus datos actuales.
              </p>
              <button onClick={loadPreferences} className="text-xs uppercase tracking-[0.2em] text-[#FF3B30] underline mt-2">
                Reintentar carga
              </button>
            </div>
          )}
          {err && <p className="text-sm text-[#FF3B30] mt-5">{err}</p>}

          <button
            onClick={submit}
            disabled={saving || loadFailed}
            title={loadFailed ? "No se pudo confirmar tu personalización actual — reintenta la carga primero" : undefined}
            data-testid="save-master-preferences-btn"
            className="mt-6 inline-flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold disabled:opacity-50 bg-[var(--brand-cyan,#FF85AC)] text-black hover:bg-[#FF9DC0]"
          >
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saved ? <><Check className="w-4 h-4" /> Guardado</> : <><Save className="w-4 h-4" /> Guardar cambios</>}
          </button>
        </article>

        <article className="glass rounded-3xl p-7 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <KeyRound className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Mi contraseña</h2>
          </div>
          <p className="text-sm text-neutral-400 mb-4">
            Mientras sigas con sesión iniciada en algún dispositivo, siempre puedes generarte una
            contraseña nueva aquí. Si algún día te quedas sin sesión activa en ningún lado y la
            olvidaste, no hay recuperación automática todavía — pídele a Claude que te la resetee
            directo en la base de datos, igual que se resetea la de un admin de agencia.
          </p>
          <button
            onClick={resetMyPassword}
            disabled={resetting}
            data-testid="reset-my-password-btn"
            className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-white/15 hover:border-[var(--brand-cyan,#FF85AC)]/50 text-sm disabled:opacity-50"
          >
            {resetting ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
            Generar contraseña nueva
          </button>
          {newPassword && (
            <div className="mt-4 p-4 rounded-2xl bg-black/40 border border-[var(--brand-cyan,#FF85AC)]/30 flex items-center justify-between gap-3" data-testid="my-new-password">
              <span className="font-mono text-white">{newPassword}</span>
              <button
                onClick={async () => { await navigator.clipboard.writeText(newPassword); setPwCopied(true); setTimeout(() => setPwCopied(false), 2000); }}
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[var(--brand-cyan,#FF85AC)] hover:underline shrink-0"
              >
                <Copy className="w-3.5 h-3.5" /> {pwCopied ? "Copiado" : "Copiar"}
              </button>
            </div>
          )}
        </article>

        <article className="glass rounded-3xl p-7 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <KeyRound className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Contraseña de otro administrador</h2>
          </div>
          <p className="text-sm text-neutral-400 mb-4">
            Resetea la contraseña de cualquier master o admin de agencia por su Booking ID — no
            hace falta entrar a editar su agencia. Para la de un viajero, usa la tarjeta
            "Viajeros" dentro del panel de esa agencia.
          </p>
          <form onSubmit={resetOtherPassword} className="flex flex-wrap gap-3">
            <input
              value={otherBookingId}
              onChange={(e) => setOtherBookingId(e.target.value.toUpperCase())}
              placeholder="BOOKING ID (ej. ADMIN-002)"
              data-testid="reset-other-booking-id"
              className="flex-1 min-w-[200px] bg-black/40 border border-white/15 focus:border-[var(--brand-cyan,#FF85AC)] outline-none rounded-xl px-4 py-2.5 text-sm text-white font-mono"
            />
            <button
              type="submit"
              disabled={resettingOther || !otherBookingId.trim()}
              data-testid="reset-other-password-btn"
              className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full border border-white/15 hover:border-[var(--brand-cyan,#FF85AC)]/50 text-sm disabled:opacity-50"
            >
              {resettingOther ? <Loader2 className="w-4 h-4 animate-spin" /> : <KeyRound className="w-4 h-4" />}
              Generar contraseña nueva
            </button>
          </form>
          {otherErr && <p className="text-sm text-[#FF3B30] mt-3" data-testid="reset-other-error">{otherErr}</p>}
          {otherResult && (
            <div className="mt-4 p-4 rounded-2xl bg-black/40 border border-[var(--brand-cyan,#FF85AC)]/30" data-testid="other-new-password">
              <p className="text-sm text-neutral-300 mb-1">
                {otherResult.traveler_name} <span className="text-neutral-500">({otherResult.role === "master" ? "master" : "admin de agencia"}, {otherResult.booking_id})</span>
              </p>
              <div className="flex items-center justify-between gap-3 mt-2">
                <span className="font-mono text-white">{otherResult.password}</span>
                <button
                  onClick={async () => { await navigator.clipboard.writeText(otherResult.password); setOtherPwCopied(true); setTimeout(() => setOtherPwCopied(false), 2000); }}
                  className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.2em] text-[var(--brand-cyan,#FF85AC)] hover:underline shrink-0"
                >
                  <Copy className="w-3.5 h-3.5" /> {otherPwCopied ? "Copiado" : "Copiar"}
                </button>
              </div>
            </div>
          )}
        </article>

        <article className="glass rounded-3xl p-7 border border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <Megaphone className="w-5 h-5 text-[var(--brand-cyan,#FF85AC)]" strokeWidth={1.5} />
            <h2 className="font-display text-xl">Promoción de plataforma completa</h2>
          </div>
          <p className="text-sm text-neutral-400 mb-5">
            Manda un push a los viajeros de <b className="text-white">todas</b> las agencias a la vez.
            Úsalo poco — cada agencia debería mandar las suyas propias desde su panel.
          </p>
          {promoErr && <p className="text-sm text-[#FF3B30] mb-4">{promoErr}</p>}
          {promoMsg && <p className="text-sm text-[var(--brand-cyan,#FF85AC)] mb-4">{promoMsg}</p>}
          <form onSubmit={sendPlatformPromo} className="grid sm:grid-cols-2 gap-4">
            <input value={promo.title} onChange={(e) => setPromo({ ...promo, title: e.target.value })}
              placeholder="Título" required data-testid="platform-promo-title"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white sm:col-span-2" />
            <input value={promo.destination} onChange={(e) => setPromo({ ...promo, destination: e.target.value })}
              placeholder="Destino" required data-testid="platform-promo-destination"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
            <input value={promo.price_from} onChange={(e) => setPromo({ ...promo, price_from: e.target.value })}
              type="number" placeholder="Precio desde" required data-testid="platform-promo-price"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white" />
            <input value={promo.subtitle} onChange={(e) => setPromo({ ...promo, subtitle: e.target.value })}
              placeholder="Subtítulo (texto del push)" data-testid="platform-promo-subtitle"
              className="bg-black/40 border border-white/15 outline-none rounded-xl px-4 py-2.5 text-sm text-white sm:col-span-2" />
            <div className="sm:col-span-2">
              <button type="submit" disabled={sendingPromo} data-testid="send-platform-promo-btn"
                className="inline-flex items-center gap-2 px-6 py-2.5 rounded-full font-semibold bg-[#FF3B30] text-white hover:bg-[#FF5147] disabled:opacity-50">
                {sendingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                Enviar a toda la plataforma
              </button>
            </div>
          </form>
        </article>
      </section>
    </Layout>
  );
}
